/**
 * Auth Bypass Provider for Development/Testing
 *
 * Enable by setting VITE_AUTH_BYPASS=true in .env.local
 * This allows browser automation tools to test without Clerk authentication
 *
 * SECURITY: This should NEVER be enabled in production!
 */

import { createContext, useContext, ReactNode } from "react";
import { ConvexProvider, ConvexReactClient } from "convex/react";

// Check if auth bypass is enabled
export const isAuthBypassEnabled = () => {
  const bypass = import.meta.env.VITE_AUTH_BYPASS;
  return bypass === 'true' || bypass === '1';
};

// Mock user for bypass mode
export const MOCK_USER = {
  id: "test_user_bypass",
  email: "test@clarity-bypass.local",
  name: "Test User (Bypass)",
  role: "admin" as const,
  isActive: true,
};

// Mock Clerk-like context for bypass mode
interface MockClerkContext {
  isSignedIn: boolean;
  isLoaded: boolean;
  user: {
    id: string;
    primaryEmailAddress?: { emailAddress: string };
    fullName?: string;
  } | null;
}

const ClerkBypassContext = createContext<MockClerkContext>({
  isSignedIn: true,
  isLoaded: true,
  user: {
    id: MOCK_USER.id,
    primaryEmailAddress: { emailAddress: MOCK_USER.email },
    fullName: MOCK_USER.name,
  },
});

// Hook that mimics useUser from @clerk/clerk-react
export function useClerkBypass() {
  return useContext(ClerkBypassContext);
}

// Hook that mimics useAuth from @clerk/clerk-react
export function useAuthBypass() {
  return {
    isSignedIn: true,
    isLoaded: true,
    userId: MOCK_USER.id,
    getToken: async () => null, // No real token in bypass mode
  };
}

interface AuthBypassProviderProps {
  children: ReactNode;
}

// Convex client for bypass mode (without Clerk)
let bypassConvex: ConvexReactClient | null = null;

function getBypassConvex() {
  if (!bypassConvex) {
    const convexUrl = import.meta.env.VITE_CONVEX_URL;
    if (convexUrl) {
      bypassConvex = new ConvexReactClient(convexUrl);
      console.log("🔓 Auth Bypass: Convex initialized without Clerk");
    }
  }
  return bypassConvex;
}

export function AuthBypassProvider({ children }: AuthBypassProviderProps) {
  const convex = getBypassConvex();

  if (!convex) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h1>Auth Bypass Error</h1>
        <p>VITE_CONVEX_URL is required for bypass mode</p>
      </div>
    );
  }

  return (
    <ClerkBypassContext.Provider
      value={{
        isSignedIn: true,
        isLoaded: true,
        user: {
          id: MOCK_USER.id,
          primaryEmailAddress: { emailAddress: MOCK_USER.email },
          fullName: MOCK_USER.name,
        },
      }}
    >
      <ConvexProvider client={convex}>
        {children}
      </ConvexProvider>
    </ClerkBypassContext.Provider>
  );
}

// Visual indicator component for bypass mode - small floating badge
export function BypassIndicator() {
  if (!isAuthBypassEnabled()) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        backgroundColor: "#f59e0b",
        color: "#000",
        padding: "4px 10px",
        fontSize: "10px",
        fontWeight: "600",
        borderRadius: "9999px",
        zIndex: 9999,
        opacity: 0.8,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      🔓 BYPASS
    </div>
  );
}

// Universal auth state hook that works in both bypass and normal mode
// This hook MUST be called unconditionally (React rules of hooks)
// Use this instead of useUser from @clerk/clerk-react in pages/components
export function useAuthState() {
  // In bypass mode, return mock state
  if (isAuthBypassEnabled()) {
    return {
      isSignedIn: true,
      isLoaded: true,
      user: {
        id: MOCK_USER.id,
        primaryEmailAddress: { emailAddress: MOCK_USER.email },
        fullName: MOCK_USER.name,
      },
    };
  }

  // This will be handled by the calling component which should conditionally
  // use Clerk's useUser - but this provides a fallback for simpler cases
  return {
    isSignedIn: false,
    isLoaded: true,
    user: null,
  };
}
